import { toast } from 'vue-sonner'
import { useI18n } from 'vue-i18n'
import axios from 'axios'

interface ProblemDetails {
  title?: string
  detail?: string
  status?: number
  code?: string
  errors?: Record<string, string[]>
}

interface JsonApiError {
  title?: string
  detail?: string
  status?: string
  code?: string
  source?: { pointer?: string }
}

interface AxiosErrorResponse {
  response?: {
    data?: ProblemDetails | { errors?: JsonApiError[] }
    status?: number
  }
}

export function useApiErrorHandler() {
  const { t } = useI18n()

  // VALIDATION_ERROR is the code EVERY field error carries; its `detail` is the
  // specific message ("Name can't be blank"). Translating the code would collapse
  // them all into one generic string — the detail always wins here.
  const FIELD_LEVEL_CODES = new Set([ 'VALIDATION_ERROR' ])

  // The house contract (doc/JSONAPI.md §11): every error carries a `code` for
  // client mapping. Prefer OUR translation of that code; fall back to the
  // server's already-localized detail (it honors X-Locale) when we have no key.
  function messageFor(code: string | undefined, detail?: string, title?: string): string {
    if (code && !FIELD_LEVEL_CODES.has(code)) {
      const key = `errors.api.${code}`
      const translated = t(key)
      if (translated !== key) return translated
    }
    return detail || title || t('errors.api.default')
  }

  // The same resolution as handleError, as a string for a caller that shows the
  // message in place (the login form) instead of in a toast. The first error
  // wins when a JSON:API response carries several.
  function messageOf(error: AxiosErrorResponse | unknown): string {
    if (!axios.isAxiosError(error)) return t('errors.api.default')

    const responseData = error.response?.data
    if (responseData && 'code' in responseData && responseData.code) {
      return messageFor(responseData.code, responseData.detail, responseData.title)
    }
    if (responseData && 'errors' in responseData && Array.isArray(responseData.errors) && responseData.errors[0]) {
      const [first] = responseData.errors
      return messageFor(first.code, first.detail, first.title)
    }
    return t('errors.api.default')
  }

  function handleError(error: AxiosErrorResponse | unknown) {
    if (!axios.isAxiosError(error)) {
      toast.error(t('errors.api.default'))
      console.error('Non-Axios error:', error)
      return
    }

    const responseData = error.response?.data

    // Handle RFC 7807 Problem Details (Identity)
    if (responseData && 'code' in responseData && responseData.code) {
      toast.error(messageFor(responseData.code, responseData.detail, responseData.title))
      return
    }

    // Handle JSON:API Errors (Resources) — code-first, same as above.
    if (responseData && 'errors' in responseData && Array.isArray(responseData.errors)) {
      responseData.errors.forEach((err: JsonApiError) => {
        toast.error(messageFor(err.code, err.detail, err.title))
      })
      return
    }

    toast.error(t('errors.api.default'))
    console.error('API Error:', error)
  }

  return { handleError, messageOf }
}
